import { profileCopy, type ProfileLanguage } from "../i18n/profile";
export const siteOrigin = (import.meta.env.VITE_SITE_URL || "https://hasanuzun.de").replace(/\/$/, "");
export const siteIndexable = import.meta.env.VITE_SITE_INDEXABLE !== "false";
export const locales = { de: "de_DE", en: "en_US", tr: "tr_TR" };
export const pageDescriptions = {
  en: "Hasan Uzun’s professional profile: software architecture, Azure cloud, system integration, modernization, and applied AI.",
  de: "Das berufliche Profil von Hasan Uzun: Softwarearchitektur, Azure Cloud, Systemintegration, Modernisierung und angewandte KI.",
  tr: "Hasan Uzun’un profesyonel profili: yazılım mimarisi, Azure bulut, sistem entegrasyonu, modernizasyon ve uygulamalı yapay zekâ.",
};
const legalTitles = { de: { imprint: "Impressum", privacy: "Datenschutz" }, en: { imprint: "Imprint", privacy: "Privacy" }, tr: { imprint: "Künye", privacy: "Gizlilik" } };
export function getPageMetadata(language: ProfileLanguage, pathname = `/${language}`) {
  const copy = profileCopy[language];
  const page = pathname.endsWith("/privacy") ? "privacy" : pathname.endsWith("/imprint") ? "imprint" : "home";
  const suffix = page === "home" ? "" : `/${page}`;
  return {
    language, page,
    title: page === "home" ? `Hasan Uzun | ${copy.role}` : `${legalTitles[language][page]} | Hasan Uzun`,
    description: page === "home" ? pageDescriptions[language] : `${legalTitles[language][page]} — Hasan Uzun.`,
    url: `${siteOrigin}/${language}${suffix}`,
    image: `${siteOrigin}/social-preview-${language}.png`,
    imageAlt: `Hasan Uzun — ${copy.headline.join(" ")}`,
    alternates: Object.keys(locales).map(lang => ({ language: lang, url: `${siteOrigin}/${lang}${suffix}` })),
    defaultUrl: `${siteOrigin}/de${suffix}`,
  };
}
export const escapeHtml = (value: string) => value.replace(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!);
export function renderMetadata(language: ProfileLanguage, pathname?: string) {
  const m = getPageMetadata(language, pathname);
  return `<title>${escapeHtml(m.title)}</title>
<meta name="robots" content="${siteIndexable ? "index, follow, max-image-preview:large" : "noindex, nofollow"}" />
<meta name="description" content="${escapeHtml(m.description)}" />
<link rel="canonical" href="${m.url}" />
${m.alternates.map(a => `<link rel="alternate" hreflang="${a.language}" href="${a.url}" />`).join("\n")}
<link rel="alternate" hreflang="x-default" href="${m.defaultUrl}" />
<meta property="og:type" content="website" />
<meta property="og:site_name" content="Hasan Uzun" />
<meta property="og:title" content="${escapeHtml(m.title)}" />
<meta property="og:description" content="${escapeHtml(m.description)}" />
<meta property="og:url" content="${m.url}" />
<meta property="og:locale" content="${locales[language]}" />
${Object.entries(locales).filter(([lang]) => lang !== language).map(([,locale]) => `<meta property="og:locale:alternate" content="${locale}" />`).join("\n")}
<meta property="og:image" content="${m.image}" />
<meta property="og:image:type" content="image/png" />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />
<meta property="og:image:alt" content="${escapeHtml(m.imageAlt)}" />
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:title" content="${escapeHtml(m.title)}" />
<meta name="twitter:description" content="${escapeHtml(m.description)}" />
<meta name="twitter:image" content="${m.image}" />
<meta name="twitter:image:alt" content="${escapeHtml(m.imageAlt)}" />`;
}
export function updatePageMetadata(language: ProfileLanguage, pathname?: string) {
  document.documentElement.lang = language;
  document.title = getPageMetadata(language, pathname).title;
  const template = document.createElement("template");
  template.innerHTML = renderMetadata(language, pathname);
  // Replace only metadata owned by this module; preserve viewport, robots, and icons.
  document.head.querySelectorAll('meta[name="robots"], meta[name="description"], meta[property^="og:"], meta[name^="twitter:"], link[rel="canonical"], link[rel="alternate"][hreflang]').forEach(node => node.remove());
  template.content.querySelector("title")?.remove();
  document.head.append(template.content);
}
